import { getTeamDetailUrl, localePath } from "@/helper/urlHelper";
import { getSeasonString, transformCountryCodeForDisplay } from "@/helper/util";
import { GameWithMatchAndPlayersAndTeams } from "@/models/Game";
import { Season } from "@/models/Season";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { FiFacebook, FiGlobe, FiInstagram, FiYoutube } from "react-icons/fi";
import { getDate, getDateWithDayLong } from "../../helper/time";
import { LeaguePlayerWithTeamAndGames } from "../../models/LeaguePlayer";
import { PlayerImage, PlayerTeaserImage, TeamImage } from "../common/Images";
import { ResponsiveContainer } from "../common/ResponsiveContainer";
import { IconTikTok } from "../common/SVG";
import { InfoCard, SocmedProps } from "../teams/TeamDetail";
import { League } from "@prisma/client";

interface PlayerDetailProps {
  player: LeaguePlayerWithTeamAndGames;
  league: League;
  season: Season;
}
export function PlayerDetail({ player, league, season }: PlayerDetailProps) {
  const { locale } = useRouter();
  const socmeds: SocmedProps[] = [
    { url: player.seasonPlayer.websiteLink, icon: FiGlobe, name: "Website" },
    {
      url: player.seasonPlayer.facebookLink,
      icon: FiFacebook,
      name: "Facebook",
    },
    {
      url: player.seasonPlayer.instagramLink,
      icon: FiInstagram,
      name: "Instagram",
    },
    { url: player.seasonPlayer.tiktokLink, icon: IconTikTok, name: "TikTok" },
    { url: player.seasonPlayer.youtubeLink, icon: FiYoutube, name: "YouTube" },
  ];
  const { t } = useTranslation();
  return (
    <>
      <div className="container container-padding-responsive lg:text-xl mb-4">
        <ResponsiveContainer>
          <div
            title={
              t(
                player.seasonPlayer.role === "Player"
                  ? "Player_Portrait"
                  : "Coach_Portrait"
              ) as string
            }
          >
            <PlayerImage player={player.seasonPlayer} size="full" />
          </div>
          <div title="Name & Links">
            <div className="bg-white lg:bg-bg-gray p-4 lg:p-0 mb-8">
              <div className="text-lg lg:text-xl">
                {player.seasonPlayer.firstName}
              </div>
              <div className="text-xl lg:text-2xl font-bold">
                {player.seasonPlayer.lastName}
              </div>
              <div className="flex">
                <div>
                  {transformCountryCodeForDisplay(
                    player.seasonPlayer.nationality
                  )}
                </div>
                {player.seasonPlayer.nationalitySecondary && (
                  <div className="ml-4">
                    {transformCountryCodeForDisplay(
                      player.seasonPlayer.nationalitySecondary
                    )}
                  </div>
                )}
              </div>
              {player.seasonPlayer.birthday && (
                <div>* {getDate(player.seasonPlayer.birthday, locale)}</div>
              )}
              {(player.seasonPlayer.height || player.seasonPlayer.weight) && (
                <div className="flex gap-4">
                  {player.seasonPlayer.height && (
                    <div>{player.seasonPlayer.height} cm</div>
                  )}
                  {player.seasonPlayer.height && player.seasonPlayer.weight && (
                    <div> /</div>
                  )}
                  {player.seasonPlayer.weight && (
                    <div>{player.seasonPlayer.weight} kg</div>
                  )}
                </div>
              )}
              <div className="flex flex-col mt-4 space-y-4 lg:flex-row lg:items-center lg:space-x-10 lg:space-y-0 lg:py-4">
                {socmeds.map((socmed, i) => {
                  return (
                    socmed.url && (
                      <Link
                        key={i}
                        href={socmed.url}
                        target="_blank"
                        className="flex gap-4 lg:block"
                      >
                        {<socmed.icon size={24} />}
                        <div className="lg:hidden">{socmed.name}</div>
                      </Link>
                    )
                  );
                })}
              </div>
            </div>
          </div>
        </ResponsiveContainer>
      </div>
      <div className="container container-padding-responsive lg:text-xl mb-4">
        <ResponsiveContainer>
          <div title={t("Player_Information") as string}>
            <div className="">
              <PlayerTeaserImage player={player.seasonPlayer} />
              <div className="bg-white lg:bg-bg-gray p-4 lg:p-0 mb-8">
                <InfoCard
                  title={t("Form_Current_Club")}
                  text={player.seasonPlayer.seasonTeam.name}
                  href={localePath(
                    locale,
                    getTeamDetailUrl({
                      league,
                      season,
                      teamId: player.seasonPlayer.seasonTeam.id,
                    })
                  )}
                  linkIsExternal={false}
                />
                {player.seasonPlayer.outfitter && (
                  <InfoCard
                    title={t("Outfitter")}
                    text={player.seasonPlayer.outfitter.company}
                    href={
                      player.seasonPlayer.outfitterLink ||
                      player.seasonPlayer.outfitter.website
                    }
                  />
                )}
                {player.seasonPlayer.hand && (
                  <InfoCard
                    title={t("Form_Hand")}
                    text={player.seasonPlayer.hand}
                  />
                )}
                {player.seasonPlayer.racketPosture && (
                  <InfoCard
                    title={t("Form_Racket_Posture")}
                    text={player.seasonPlayer.racketPosture}
                  />
                )}
                {player.seasonPlayer.role === "Player" && (
                  <InfoCard title="Spielerrangliste TTBL" text={`#${player.rank}`} />
                )}
                {player.seasonPlayer.worldRank && (
                  <InfoCard
                    title={t("Form_World_Ranking")}
                    text={player.seasonPlayer.worldRank.toString()}
                  />
                )}
                {player.seasonPlayer.qttrValue && (
                  <InfoCard
                    title="Q-TTR-Wert"
                    text={player.seasonPlayer.qttrValue.toString()}
                  />
                )}
              </div>
            </div>
          </div>
          <div title={`${t("Season")} ${getSeasonString(season)}`}>
            <div>
              {player.games.map((g) => (
                <GameCard game={g} key={g.id} />
              ))}
            </div>
          </div>
        </ResponsiveContainer>
      </div>
    </>
  );
}

interface GameCardProps {
  game: GameWithMatchAndPlayersAndTeams;
}
function GameCard({ game }: GameCardProps) {
  const { locale } = useRouter();
  return (
    <div className="bg-white p-4 my-1 lg:my-4">
      <div className="mb-4">{getDateWithDayLong(game.match.timeStamp, locale)}</div>
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <PlayerImage player={game.homePlayer?.seasonPlayer} />
          <TeamImage
            team={game.homePlayer?.seasonPlayer.seasonTeam}
            size="md"
            className="bg-bg-gray rounded-lg"
          />
        </div>
        <div className="text-xl lg:text-4xl">
          {`${game.homeSets} : ${game.awaySets}`}
        </div>
        <div className="flex items-center">
          <PlayerImage player={game.awayPlayer?.seasonPlayer} />
          <TeamImage
            team={game.awayPlayer?.seasonPlayer.seasonTeam}
            size="md"
            className="bg-bg-gray rounded-lg"
          />
        </div>
      </div>
    </div>
  );
}
